// Bits shared by the two admin pages (page-admin, page-admin-teams): the
// display-unit converters and the in-page tab strip that links the pages
// together. The wire (api/admin.ts) always speaks bytes and seconds; the UI
// edits and shows MiB and minutes, so every form field converts through here —
// keeping the round-trip in one place means an operator who types 512 always
// reads back 512.

import { html, css } from "lit";
import { navigate } from "../nav.js";

const MIB = 1024 * 1024;

/** mibToBytes converts a UI MiB value to the wire's bytes. */
export function mibToBytes(mib: number): number {
  return Math.round(mib * MIB);
}

/** bytesToMib converts wire bytes to whole-number-friendly MiB for display. */
export function bytesToMib(bytes: number): number {
  return bytes / MIB;
}

/** minutesToSeconds converts a UI minutes value to the wire's seconds. */
export function minutesToSeconds(min: number): number {
  return Math.round(min * 60);
}

/** secondsToMinutes converts wire seconds to minutes for display. */
export function secondsToMinutes(sec: number): number {
  return sec / 60;
}

/** The styles behind adminTabs; add to a page's static styles. */
export const adminTabsStyles = css`
  .admin-tabs {
    display: flex;
    gap: 2px;
    margin-bottom: var(--bino-space-lg);
    border-bottom: 1px solid var(--bino-border);
  }
  .admin-tab {
    font-family: var(--bino-font-sans);
    font-size: var(--bino-font-size-md);
    font-weight: 500;
    color: var(--bino-text-secondary);
    padding: 0.5rem 0.8125rem;
    border: none;
    border-bottom: 2px solid transparent;
    background: none;
    cursor: pointer;
    transition: color var(--bino-transition-fast);
  }
  .admin-tab:hover {
    color: var(--bino-text);
  }
  .admin-tab.active {
    color: var(--bino-active-text);
    font-weight: 600;
    border-bottom-color: var(--bino-primary);
  }
`;

const TABS = [
  { key: "runtime", label: "Runtime", href: "/admin" },
  { key: "teams", label: "Team limits", href: "/admin/teams" },
] as const;

export type AdminTab = (typeof TABS)[number]["key"];

/** adminTabs renders the Runtime | Team limits strip; `active` marks the page. */
export function adminTabs(active: AdminTab) {
  return html`<div class="admin-tabs" role="tablist" aria-label="Admin sections">
    ${TABS.map(
      (t) => html`<button
        class="admin-tab ${t.key === active ? "active" : ""}"
        role="tab"
        aria-selected=${t.key === active ? "true" : "false"}
        @click=${() => navigate(t.href)}
      >
        ${t.label}
      </button>`,
    )}
  </div>`;
}
